<template>
  <div>
    <b-modal
      v-model="showModal"
      title="Lịch sử"
      size="xl"
      hide-footer
      @hidden="onHidden"
    >
      <div v-if="loading" class="text-center py-5">
        <div class="spinner spinner-primary"></div>
      </div>
      <div v-else-if="historyItems.length === 0" class="text-center py-5">
        <p class="text-muted">Chưa có lịch sử</p>
      </div>
      <b-container v-else class="bv-example-row">
        <b-table
          :fields="historyFields"
          :items="historyItems"
          class="table-bordered table-hover"
        >
          <template v-slot:cell(updatedBy)="row">
            <div class="d-flex flex-column">
              <strong>{{ row.item.createdByName }}</strong>
            </div>
          </template>
          <template v-slot:cell(action)="row">
            <b-badge :variant="getActionVariant(row.item.action)">
              {{ row.item.actionName }}
            </b-badge>
          </template>
          <template v-slot:cell(info)="row">
            <div
              v-for="(c, idx) in row.item.changeLines"
              :key="`chg-${row.item.id}-${idx}-${c.key}`"
              class="text-muted mt-1"
              style="font-size: 12px"
            >
              <strong>{{ c.label }}: </strong>
              <span v-if="row.item.action !== HISTORY_ACTION_TYPE.CREATE" class="history-change-value history-change-value-old">{{ c.oldText }}</span>
              <span v-if="row.item.action !== HISTORY_ACTION_TYPE.CREATE"> → </span>
              <span class="history-change-value">{{ c.newText }}</span>
            </div>
          </template>
          <template v-slot:cell(userAgent)="row">
            <div class="d-flex flex-column">
              <span><strong>IP:</strong> {{ row.item.userIP }}</span>
              <span class="text-muted">
                OS: {{ row.item.userAgent?.osName || '--' }}
              </span>
              <span class="text-muted">
                Browser: {{ row.item.userAgent?.browserName || '--' }}
              </span>
            </div>
          </template>
          <template v-slot:cell(detail)="row">
            <div
              class="orderCode"
              @click.stop="openHistoryDetailModal(row.item.id)"
              style="cursor: pointer; color: #3699ff; font-weight: 500"
            >
              #{{ row.item.id }}
            </div>
          </template>
        </b-table>
      </b-container>
    </b-modal>
    <warranty-history-detail-modal
      :show="showHistoryDetailModal"
      :history-id="selectedHistoryId"
      @update:show="showHistoryDetailModal = $event"
      @open-modal="handleOpenModalFromUrl"
    />
  </div>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { HISTORY_ACTION_TYPE } from '@/utils/enum';
import { HISTORY_ACTION_TYPE_NAME } from '@/utils/enum-name';
import { WARRANTY_RECEPTION_STATUS_NAME } from '@/utils/enum-name';
import moment from 'moment';
import WarrantyHistoryDetailModal from './WarrantyHistoryDetailModal.vue';
import { convertPrice } from '@/utils/common';

export default {
  name: 'WarrantyHistoryModal',
  components: {
    WarrantyHistoryDetailModal,
  },
  props: {
    warrantyId: {
      type: [Number, String],
      default: null,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      historyItems: [],
      loading: false,
      showHistoryDetailModal: false,
      selectedHistoryId: null,
      historyFields: [
        {
          key: 'updatedBy',
          label: 'Người thao tác',
          thStyle: { textAlign: 'center', width: '20%' },
          tdClass: 'text-center',
        },
        {
          key: 'createdAt',
          label: 'Lúc',
          thStyle: { textAlign: 'center', width: '20%' },
          tdClass: 'text-center',
        },
        {
          key: 'action',
          label: 'Hành động',
          sortable: false,
          thStyle: {
            textAlign: 'center',
            color: 'rgb(24, 28, 50)',
            width: '20%',
          },
          tdClass: 'text-center',
        },
        {
          key: 'info',
          label: 'Chi tiết',
          sortable: false,
          thStyle: {
            textAlign: 'center',
            color: 'rgb(24, 28, 50)',
            width: '40%',
          },
        },
        {
          key: 'detail',
          label: '#',
          sortable: false,
          thStyle: { textAlign: 'center', color: 'rgb(24, 28, 50)' },
          tdClass: 'text-center',
        },
      ],
       HISTORY_ACTION_TYPE,
    };
  },
  computed: {
    showModal: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
  },
  watch: {
    showModal(newVal) {
      if (newVal && this.warrantyId) {
        this.fetchHistory();
      }
    },
    warrantyId: {
      immediate: true,
      handler(newId) {
        if (newId && this.showModal) {
          this.fetchHistory();
        }
      },
    },
  },
  mounted() {
    this.handleModalFromUrl();
  },
  methods: {
    convertPrice,
    async fetchHistory() {
      if (!this.warrantyId) {
        this.historyItems = [];
        return;
      }

      this.loading = true;
      try {
        const response = await ApiService.get(
          `v1/warranties/${this.warrantyId}/histories`,
        );
        const resData = response.data.data || [];
        this.historyItems = resData.map((data) => {
          const actionName =
            HISTORY_ACTION_TYPE_NAME[data.action] || 'Cập nhật';
            let userAgent = {}
          try {
            userAgent = JSON.parse(data.userAgent);
          } catch (error) {
            userAgent = {};
          }

          const originalObj = this._safeJsonParse(data.originalValue)
          const newObj = this._safeJsonParse(data.newValue)
          const changeLines = this._buildChangeLines({
            changes: data.changes || {},
            originalValue: originalObj,
            newValue: newObj,
          })
          return {
            id: data.id,
            type: data.type,
            referenceId: data.referenceId,
            action: data.action,
            originalValue: originalObj,
            newValue: newObj,
            updatedBy: data.updatedBy,
            userAgent,
            userIP: data.userIP,
            createdByName: data.createdByName,
            createdAt: moment(String(data.createdAt)).format(
              'DD/MM/YYYY HH:mm',
            ),
            updatedAt: moment(data.updatedAt).format('DD/MM HH:mm'),
            actionName,
            changeLines,
          };
        });
      } catch (err) {
        console.error('Error fetching history:', err);
        this.historyItems = [];
      } finally {
        this.loading = false;
      }
    },
    openHistoryDetailModal(historyId) {
      this.selectedHistoryId = historyId;
      this.showHistoryDetailModal = true;
    },
    handleOpenModalFromUrl(historyId) {
      this.selectedHistoryId = historyId;
      this.showHistoryDetailModal = true;
    },
    handleModalFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      const modal = urlParams.get('modal');
      const historyId = urlParams.get('id');

      if (modal === 'warranty-history' && historyId) {
        this.openHistoryDetailModal(historyId);
      }
    },
    onHidden() {
      this.$emit('input', false);
    },
    getActionVariant(action) {
      if (action === HISTORY_ACTION_TYPE.CREATE) {
        return 'success';
      }
      if (action === HISTORY_ACTION_TYPE.UPDATE) {
        return 'warning';
      }
      if (action === HISTORY_ACTION_TYPE.DELETE) {
        return 'danger';
      }
      return 'secondary';
    },
    _safeJsonParse(value) {
      try {
        return typeof value === 'string' && value ? JSON.parse(value) : value || {}
      } catch (e) {
        return {}
      }
    },
    _buildChangeLines({ changes, originalValue, newValue }) {
      const o = originalValue || {}
      const n = newValue || {}
      const lines = []
      const declaredFields = [
        'statusName',
        'transferStatusName',
        'warrantyServiceName',
        'estimatedCost',
        'actualCost',
        'supportCost',
        'expectedReturnDate',
        'actualReturnDate',
        'storeName',
        'previousStoreName',
        'issueDescription',
        'internalNote',
        'externalNote',
        'rejectReason',
        'imeiCode',
        'productName',
      ]
      const declaredSet = new Set(declaredFields)
      const hasChangesFromBe = changes && typeof changes === 'object' && Object.keys(changes).length > 0

      if (hasChangesFromBe) {
        Object.keys(changes).forEach((key) => {
          if (!declaredSet.has(key)) return
          const item = changes[key]
          if (!item || typeof item !== 'object') return
          const oldText = this._formatFieldValue(key, item.oldValue)
          const newText = this._formatFieldValue(key, item.newValue)
          if (String(oldText) !== String(newText)) {
            lines.push({
              key,
              label: this._fieldLabel(key),
              oldText: oldText || '—',
              newText: newText || '—',
            })
          }
        })
        return lines
      }

      const allKeys = declaredFields
      allKeys.forEach((key) => {
        const oldText = this._formatFieldValue(key, o[key])
        const newText = this._formatFieldValue(key, n[key])
        if (String(oldText) !== String(newText)) {
          lines.push({
            key,
            label: this._fieldLabel(key),
            oldText: oldText || '—',
            newText: newText || '—',
          });
        }
      });
      return lines
    },
    _fieldLabel(key) {
      const map = {
        statusName: 'Trạng thái',
        warrantyServiceName: 'Loại dịch vụ',
        estimatedCost: 'Báo giá',
        actualCost: 'Tiền thu khách',
        supportCost: 'Phí hỗ trợ bảo hành',
        expectedReturnDate: 'Ngày hẹn trả',
        actualReturnDate: 'Ngày trả thực tế',
        storeName: 'Cửa hàng',
        previousStoreId: 'Cửa hàng trước',
        previousStoreName: 'Cửa hàng trước',
        transferStatusName: 'Trạng thái chuyển CH',
        imeiCode: 'IMEI/Serial',
        rejectReason: 'Lý do từ chối',
        issueDescription: 'Mô tả lỗi',
        internalNote: 'Ghi chú nội bộ',
        externalNote: 'Ghi chú ngoại bộ',
        productName: 'Sản phẩm',
      }
      return map[key] || key
    },
    _formatFieldValue(key, value) {
      if (value == null || value === '') return ''
      if (['status', 'statusName'].includes(key)) {
        return WARRANTY_RECEPTION_STATUS_NAME[value] || value
      }
      if (['estimatedCost', 'actualCost', 'repairCost'].includes(key)) {
        return this.convertPrice(Number(value || 0))
      }
      return String(value)
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .modal-body {
  padding: 1.5rem;
}

.history-change-value {
  color: #b45309;
}

.history-change-value-old {
  color: rgb(107 114 128);
  text-decoration: line-through;
}
</style>
